import React, { useState, useEffect } from 'react';
import { auth } from '../../firebase/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const apiDataUrl = apiUrl + '/api/v1/purchase-data';

const BuyData = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);

  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [dataOptions, setDataOptions] = useState([]);
  const [selectedDataOption, setSelectedDataOption] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');

  const [message, setMessage] = useState(null);
  const [dataPlansDb, setDataPlansDb] = useState({});
  const [allNetworks, setAllNetworks] = useState({});

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const getMessageColor = (msg) => {
    if (!msg) return '';
    const lowerMsg = String(msg).toLowerCase();
    if (lowerMsg.includes('success')) return 'text-green-500';
    if (lowerMsg.includes('insufficient') || lowerMsg.includes('try again') || lowerMsg.includes('warning')) return 'text-yellow-500';
    return 'text-red-500';
  };

  useEffect(() => {
    const fetchDataPlans = async () => {
      const CACHE_KEY = 'vtu_data_plans_cache';
      const CACHE_TIME_KEY = 'vtu_data_plans_cache_time';
      const CACHE_DURATION = 12 * 60 * 60 * 1000; // 12 hours

      const cachedData = localStorage.getItem(CACHE_KEY);
      const cacheTime = localStorage.getItem(CACHE_TIME_KEY);
      const now = new Date().getTime();

      const parseAndSetData = (data) => {
        setDataPlansDb(data);
        const networks = {};
        Object.keys(data).forEach(network => {
          networks[network] = Object.keys(data[network]);
        });
        setAllNetworks(networks);
      };

      if (cachedData && cacheTime && (now - parseInt(cacheTime) < CACHE_DURATION)) {
        parseAndSetData(JSON.parse(cachedData));
      } else {
        window.dispatchEvent(new CustomEvent('dataFetchStatus', { detail: { isFetching: true } }));
        try {
          const response = await fetch(`${apiUrl}/api/v1/data-plans`);
          if (response.ok) {
            const data = await response.json();
            parseAndSetData(data);
            localStorage.setItem(CACHE_KEY, JSON.stringify(data));
            localStorage.setItem(CACHE_TIME_KEY, now.toString());
          } else {
            console.log("Failed to fetch data plans from backend.");
          }
        } catch (error) {
          console.error("Error fetching data plans from backend: ", error);
        } finally {
          window.dispatchEvent(new CustomEvent('dataFetchStatus', { detail: { isFetching: false } }));
        }
      }
    };
    fetchDataPlans();
  }, []);


  //function to update network on changed
  const handleNetworkChange = (e) => {
    const selected = e.target.value
    setSelectedNetwork(selected);
    setSelectedPlan('');
    setDataOptions([]);

    if (!selected) {
      return;
    }
  };

  // function to update plan selection and update all data options
  const handlePlanChange = (e) => {
    const selected = e.target.value;
    setSelectedPlan(selected);

    if (!selected) {
      setDataOptions([]);
      return;
    }

    if (dataPlansDb[selectedNetwork] && dataPlansDb[selectedNetwork][selected]) {
      setDataOptions(dataPlansDb[selectedNetwork][selected]);
      console.log("data options = ", dataPlansDb[selectedNetwork][selected])
    } else {
      console.log("No options found for this plan in DB");
      setDataOptions([]);
    }
  };

  const handleOptionChange = (e) => {
    setSelectedDataOption(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      if (!user) {
        setMessage('Please log in to make a purchase.');
        setLoading(false);
        return;
      }

      const networkMap = { "MTN": 1, "GLO": 2, "9MOBILE": 3, "AIRTEL": 4 };
      const selectedDataOptionObj = dataOptions.find((option) => option.plan_id === selectedDataOption);

      if (!selectedNetwork || !selectedDataOptionObj) {
        setMessage('Invalid network or data option selected.');
        setLoading(false);
        return;
      }

      const dataAmount = parseInt(selectedDataOptionObj.price, 10);

      const networkCode = networkMap[selectedNetwork];

      if (!networkCode) {
        setMessage('Invalid network provider.');
        setLoading(false);
        return;
      }


      const idToken = await user.getIdToken();
      const clientRef = `txn_${new Date().getTime()}_${Math.floor(Math.random() * 1000)}`;

      const payload = {
        network: networkCode,
        dataplan: parseInt(selectedDataOptionObj.plan_id, 10),
        mobileno: mobileNumber,
        client_reference: clientRef,
        max_amount_payable: dataAmount
      };

      const response = await fetch(apiDataUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setMessage(data.status || 'Transaction successful');
        console.log("response data =  " + JSON.stringify(data));
      } else {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = 'Failed to complete transaction';
        
        if (errorData.detail) {
            if (typeof errorData.detail === 'string') {
                errorMessage = errorData.detail;
            } else if (Array.isArray(errorData.detail)) {
                errorMessage = errorData.detail.map(err => `${err.loc[err.loc.length - 1]}: ${err.msg}`).join(', ');
            } else if (typeof errorData.detail === 'object') {
                errorMessage = errorData.detail.message || JSON.stringify(errorData.detail);
            }
        } else if (errorData.message) {
            errorMessage = errorData.message;
        }
        
        // robust check for insufficient balance anywhere in the error data
        const errorString = JSON.stringify(errorData).toLowerCase();
        if (errorString.includes('insufficient') && errorString.includes('balance')) {
            errorMessage = 'Please try again later';
        }
        
        setMessage(errorMessage);
      }
    } catch (error) {
      console.error('Error Processing data:', error);
      setMessage(error.message || 'Error with data purchase.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className='bg-light2 dark:bg-dark1 text-dark0 dark:text-light1 space-y-6 w-full py-4 px-8 rounded-md'>
      <div className='flex flex-col items-start space-y-2'>
        <h2 className='text-primary bg-light1 dark:bg-dark2 px-8 py-1 rounded-2xl'>Data Purchase</h2>
        <hr className='border-dark0 w-full' />
      </div>

      {message && <p className={getMessageColor(message)}>{message}</p>}

      <div>
        <label>Network Provider:</label>
        <select className='outline-none border-none dark:bg-dark2 rounded-md h-10 px-2 w-full' value={selectedNetwork} onChange={handleNetworkChange} required disabled={loading}>
          <option value=''>Select Network</option>
          {
            Object.keys(allNetworks).map((network) => (
              <option key={network} value={network}>{network}</option>
            ))
          }
        </select>
      </div>

      <div>
        <label>Data Type:</label>
        <select className='outline-none border-none dark:bg-dark2 rounded-md h-10 px-2 w-full' value={selectedPlan} onChange={handlePlanChange} required disabled={loading}>
          <option value=''>Select Plan</option>
          {allNetworks[selectedNetwork] && allNetworks[selectedNetwork].map((plan) => (
            <option key={plan} value={plan}>{plan.replace(/_/g, ' ').toUpperCase()}</option>
          ))}
        </select>
      </div>

      <div>
        <label>Data Options:</label>
        <select className='outline-none border-none dark:bg-dark2 rounded-md h-10 px-2 w-full' value={selectedDataOption} onChange={handleOptionChange} required disabled={!selectedNetwork || loading}>
          <option value=''>Select data option</option>
          {dataOptions.map((option) => (
            <option className='flex space-between' key={option.plan_id} value={option.plan_id}>{option.name} - ₦{option.price}  ({option.validity})</option>
          ))}
        </select>
      </div>

      <div>
        <label>Phone Number:</label>
        <input className='outline-none border-none dark:bg-dark2 rounded-md h-10 px-2 w-full' type='tel' value={mobileNumber} onChange={(e) => setMobileNumber(e.target.value)} placeholder='Enter mobile number' required disabled={loading} />
      </div>

      <button type='submit' disabled={loading}>{loading ? 'Processing...' : 'Purchase Data'}</button>
    </form>
  );
};

export default BuyData;
